import {
  constructionBudget,
  financedHoldback,
} from "../sizersShared/sizerAmounts";

/**
 * gucLoanMath — the GUC-only lever on top of the shared loan-size build-up.
 *
 * The build-up itself (basis, advance, holdback, financed closing costs, total,
 * and which amounts to show once priced) lives in sizersShared/sizerAmounts.js,
 * ONE copy for the RTL and GUC sizers so the two cannot disagree. It is
 * re-exported here so existing imports keep working.
 */
export {
  isPurchasePurpose,
  costBasis,
  constructionBudget,
  financedHoldback,
  estimateAmounts,
  pricedAmounts,
} from "../sizersShared/sizerAmounts";

/**
 * The budget-share cure, as a form input.
 *
 * When a TOTAL-loan metric (LTARV / TLTC) is the binding one, the lever that is
 * not "borrow less at close" is "finance less of the budget": hold the advance
 * where it is and let the holdback fall until the total fits. The engine's own
 * `suggestions[]` gives `maxLoanAtMax` — the total loan at the reduced maximum —
 * so the target holdback is that minus the advance, and everything below is the
 * arithmetic that turns a target holdback back into the percentage the form
 * actually stores.
 *
 * Returns null when the lever cannot reach (no budget, or the total is already
 * over even with the budget financed at 0%), so the button is simply not
 * offered rather than offered and wrong.
 */
export function budgetShareCure({ values, advance, maxTotalLoan }) {
  const budget = constructionBudget(values);
  if (!budget || !maxTotalLoan || !advance) return null;
  const targetHoldback = maxTotalLoan - advance;
  if (targetHoldback < 0) return null;
  const currentHoldback = financedHoldback(values);
  if (targetHoldback >= currentHoldback) return null;
  const pct = Math.max(0, Math.min(1, targetHoldback / budget));
  return {
    financedBudgetPercent: Math.floor(pct * 10000) / 10000,
    holdback: targetHoldback,
    // What the borrower brings instead — the unfinanced share of the budget.
    borrowerCash: budget - targetHoldback,
    total: advance + targetHoldback,
  };
}
